import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Upload, FileCheck2, CheckCircle, Loader2, FileText } from 'lucide-react';
import { doc, getDoc, updateDoc, serverTimestamp, collection, getDocs, addDoc } from "firebase/firestore";
import MICPreviewContent from './MICPreviewContent.js';
import MultiFileUpload from './components/MultiFileUpload.js';
import Spinner from './Spinner.js';
import { storage, ref, uploadBytes, getDownloadURL } from './firebaseUtils.js';

const vehicleTypeOptions = ['TATA ACE', '3 WHEELER', 'TATA 407', 'PICKUP - 8 FEET', 'PICKUP - 9 FEET', 'TRUCK - 14 FEET', 'TRUCK - 19 FEET', 'TRUCK - 20 FEET', 'TRUCK - 32 FEET', 'TRUCK - 40 FEET', 'PART LOAD', 'COURIER', 'SELF PICKUP', '2 Wheeler'];
const transportOptions = ['#PORT', '#WHEELS-EYE', '#WINS', '#J&K TRP', '#OLA', '#HEMANT', '#ORANGE CARGO', '#SONU', '#VISHNU', '#JAIN TRP', '#KABRA EXP', '#SELF', '#TCI', '#FASTWAY', '#RIVIGO', '#RK TRP AHM', '#TRACKON', '#TRUKKY', '#MARUTI', '#DELHIVERY', '#Anjani courier services', '#Hemant logistics', '#MMTPL', '#VRL', '#ANSUMA', '#YUSUF', '#JAIPUR GOLDEN', '#GUPTA', '#Courier', '#Manoj Cargo', '#Patel Logistics', '#Prime Translog', '#Kamlesh', '#Jet Cargo', '#Momai Roadways Service', '#Metro Cargo and Movers', '#Momai Roadways', '#JEET ROADWAYS'];
const checklistFields = [
  { key: 'informedToHo', label: 'INFORMED TO HO FOR MISSING AND DAMAGES (WHATSAPP / SLACK)' },
  { key: 'paperSharedToOffice', label: 'PAPER SHARED TO OFFICE (WHATSAPP / SLACK)' },
  { key: 'damagePartsSentForMaintenance', label: 'DAMAGE PARTS SENT FOR MAINTENANCE (IF ANY)' },
  { key: 'invoicePapersSentToAccounts', label: 'INVOICE / PAPERS SENT TO ACCOUNTS' }
];

const toDisplayValue = (value, fallback = '---') => {
  const stringValue = String(value ?? '').trim();
  return stringValue || fallback;
};

const getCustomerBillingEmail = (customer) => {
  const billingAddressEmail = customer?.addresses?.find((address) => address?.address_type === 'Billing')?.email;
  return billingAddressEmail || customer?.billing_email || customer?.email || customer?.contact_email || '';
};

const getCustomerShippingEmail = (customer, mic) => {
  const shippingAddressEmail = customer?.addresses?.find((address) => address?.address_type === 'Shipping')?.email;
  return (
    mic?.shippingPersonEmail ||
    mic?.shipToEmail ||
    mic?.projectEmail ||
    shippingAddressEmail ||
    customer?.shipping_email ||
    customer?.contact_email ||
    customer?.email ||
    ''
  );
};

const buildEmailAttachments = (...fileGroups) => {
  const seen = new Set();

  return fileGroups
    .flat()
    .filter((file) => file?.url)
    .map((file, index) => {
      const key = file.url;
      if (seen.has(key)) return null;
      seen.add(key);
      const extensionFromType = String(file.type || '').includes('/') ? `.${String(file.type).split('/').pop()}` : '';
      const fallbackName = `attachment_${index + 1}${extensionFromType}`;
      return {
        filename: file.name || fallbackName,
        path: file.url
      };
    })
    .filter(Boolean);
};


const InboundQCDetail = ({ db, user }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [qcFiles, setQcFiles] = useState([]);
  const [existingQcFiles, setExistingQcFiles] = useState([]);
  const [qcRemarks, setQcRemarks] = useState('');
  const [currentStatus, setCurrentStatus] = useState('');
  const [mic, setMic] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [firms, setFirms] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [vehicleType, setVehicleType] = useState('');
  const [transportName, setTransportName] = useState('');
  const [weightInKgs, setWeightInKgs] = useState('');
  const [missingDamages, setMissingDamages] = useState('no');
  const [vehicleImageProofFiles, setVehicleImageProofFiles] = useState([]);
  const [existingVehicleImageProofFiles, setExistingVehicleImageProofFiles] = useState([]);
  const [missingDamageFiles, setMissingDamageFiles] = useState([]);
  const [existingMissingDamageFiles, setExistingMissingDamageFiles] = useState([]);
  const [inwardQcChecklist, setInwardQcChecklist] = useState({
    informedToHo: false,
    paperSharedToOffice: false,
    damagePartsSentForMaintenance: false,
    invoicePapersSentToAccounts: false
  });
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    const fetchInboundQc = async () => {
      setLoading(true);
      try {
        const docSnap = await getDoc(doc(db, "inwards", id));
        if (!docSnap.exists()) {
          alert("MIC not found");
          navigate('/ops/inbound-qc');
          return;
        }

        const data = docSnap.data();
        const micData = {
          id: docSnap.id,
          ...data,
          components: data.micComponents || []
        };

        setMic(micData);
        setExistingQcFiles(data.qcFiles || []);
        setQcRemarks(data.qcRemarks || '');
        setCurrentStatus(data.status || '');
        setVehicleType(data.vehicleType || '');
        setTransportName(data.transportName || data.transport || '');
        setWeightInKgs(data.weightInKgs || '');
        setMissingDamages(data.missingDamages || 'no');
        setExistingVehicleImageProofFiles(data.vehicleImageProofFiles || []);
        setExistingMissingDamageFiles(data.missingDamageFiles || []);
        setInwardQcChecklist({
          informedToHo: Boolean(data.inwardQcChecklist?.informedToHo),
          paperSharedToOffice: Boolean(data.inwardQcChecklist?.paperSharedToOffice),
          damagePartsSentForMaintenance: Boolean(data.inwardQcChecklist?.damagePartsSentForMaintenance),
          invoicePapersSentToAccounts: Boolean(data.inwardQcChecklist?.invoicePapersSentToAccounts)
        });

        if (data.customerID) {
          const customerSnap = await getDoc(doc(db, "customers", data.customerID));
          if (customerSnap.exists()) {
            setCustomer(customerSnap.data());
          }
        }

        const [warehouseSnap, firmSnap] = await Promise.all([
          getDocs(collection(db, "warehouses")),
          getDocs(collection(db, "firms"))
        ]);
        setWarehouses(warehouseSnap.docs.map((warehouseDoc) => ({ id: warehouseDoc.id, ...warehouseDoc.data() })));
        setFirms(firmSnap.docs.map((firmDoc) => ({ id: firmDoc.id, ...firmDoc.data() })));
      } catch (error) {
        console.error("Error loading inbound QC detail:", error);
        alert("Failed to load inbound QC detail.");
      } finally {
        setLoading(false);
      }
    };

    fetchInboundQc();
  }, [db, id, navigate]);

  const uploadFiles = async (files, existingFiles, folderName) => {
    if (files.length === 0) return existingFiles;

    const uploadedFiles = await Promise.all(
      files.map(async (file) => {
        const safeName = (file.name || 'file').replace(/[^\w.\-() ]+/g, '_');
        const path = `inbound_qc/${id}/${folderName}/${Date.now()}_${safeName}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        return { name: file.name, type: file.type, size: file.size, url };
      })
    );

    return uploadedFiles;
  };

  const handleChecklistChange = (field) => {
    setValidationErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setInwardQcChecklist((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const getFieldStyle = (fieldName, baseStyle = {}) => ({
    ...baseStyle,
    ...(validationErrors[fieldName] ? errorInputStyle : {})
  });

  const clearValidationError = (fieldName) => {
    setValidationErrors((prev) => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const validateBeforeConfirm = () => {
    const nextErrors = {};

    if (!(qcFiles.length > 0 || existingQcFiles.length > 0)) nextErrors.qcFiles = true;
    if (!String(qcRemarks || '').trim()) nextErrors.qcRemarks = true;
    if (!String(vehicleType || '').trim()) nextErrors.vehicleType = true;
    if (!String(transportName || '').trim()) nextErrors.transportName = true;
    if (!String(weightInKgs || '').trim()) nextErrors.weightInKgs = true;
    if (!String(missingDamages || '').trim()) nextErrors.missingDamages = true;
    if (!(vehicleImageProofFiles.length > 0 || existingVehicleImageProofFiles.length > 0)) nextErrors.vehicleImageProofFiles = true;
    if (missingDamages === 'yes' && !(missingDamageFiles.length > 0 || existingMissingDamageFiles.length > 0)) nextErrors.missingDamageFiles = true;

    checklistFields.forEach((field) => {
      if (!inwardQcChecklist[field.key]) nextErrors[field.key] = true;
    });

    setValidationErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleConfirmQc = async () => {
    if (!validateBeforeConfirm()) {
      alert("Please complete all required Inwards QC fields before confirming.");
      return;
    }
    if (!window.confirm("Confirm this QC?")) return;

    setIsSaving(true);
    try {
      const uploadedQcFiles = await uploadFiles(qcFiles, existingQcFiles, 'qc_files');
      const uploadedVehicleImageProofFiles = await uploadFiles(vehicleImageProofFiles, existingVehicleImageProofFiles, 'vehicle_image_proof');
      const uploadedMissingDamageFiles = missingDamages === 'yes'
        ? await uploadFiles(missingDamageFiles, existingMissingDamageFiles, 'missing_damage_files')
        : [];
      const userName = user.displayName || user.email;

      const payload = {
        status: 'qc_completed',
        qcRemarks,
        qcFiles: uploadedQcFiles,
        vehicleType,
        transportName,
        weightInKgs,
        missingDamages,
        vehicleImageProofFiles: uploadedVehicleImageProofFiles,
        missingDamageFiles: uploadedMissingDamageFiles,
        inwardQcChecklist,
        updatedAt: serverTimestamp(),
        updatedBy: userName,
        qcCompletedBy: userName,
        qcCompletedAt: serverTimestamp()
      };

      await updateDoc(doc(db, "inwards", id), payload);

      const customerName = toDisplayValue(mic?.customerName || mic?.supplierName);
      const projectName = toDisplayValue(mic?.siteProjectName || mic?.projectName || mic?.project || '', '');
      const matchedFirm = firms.find((firm) => firm.id === mic?.firmId);
      const fromFirm = toDisplayValue(
        mic?.firmName ||
        matchedFirm?.name ||
        matchedFirm?.display_name ||
        matchedFirm?.company_name ||
        mic?.organisation ||
        mic?.companyName ||
        mic?.firmId
      );
      const officeBillingEmail = toDisplayValue(getCustomerBillingEmail(customer));
      const siteShippingEmail = toDisplayValue(getCustomerShippingEmail(customer, mic));
      const verifiedByFinance = toDisplayValue(mic?.verifiedByFinance || mic?.verifiedBy || userName);

      const emailAttachments = buildEmailAttachments(
        uploadedVehicleImageProofFiles,
        uploadedMissingDamageFiles,
        mic?.mic_completionFiles || [],
        mic?.completionFiles || [],
        uploadedQcFiles
      );

      let emailQueued = false;
      try {
        await addDoc(collection(db, "mail"), {
          to: 'accounts@nexriseindia.in',
          cc: 'sales@nexriseindia.in',
          message: {
            attachments: emailAttachments
          },
          template: {
            name: 'inwards_email',
            data: {
              pickupDate: toDisplayValue(mic?.pickupDate || mic?.micDate),
              micNo: toDisplayValue(mic?.micNo || mic?.inwardNo),
              pickupChallanNo: toDisplayValue(mic?.referenceNo || mic?.inwardNo),
              fromFirm,
              warehouse: toDisplayValue(mic?.warehouse),
              typeOfOrder: toDisplayValue(mic?.typeOfMIC),
              customerName,
              projectName: projectName || '---',
              siteShippingEmail,
              officeBillingEmail,
              materialWeight: toDisplayValue(weightInKgs),
              missingDamages: toDisplayValue(missingDamages),
              pickupAgent: toDisplayValue(mic?.pickupAgent || mic?.preparedBy),
              verifiedByFinance
            }
          }
        });
        emailQueued = true;
      } catch (emailError) {
        console.error("Error queueing inward QC email:", emailError);
      }

      setExistingQcFiles(uploadedQcFiles);
      setExistingVehicleImageProofFiles(uploadedVehicleImageProofFiles);
      setExistingMissingDamageFiles(uploadedMissingDamageFiles);
      setQcFiles([]);
      setVehicleImageProofFiles([]);
      setMissingDamageFiles([]);
      setCurrentStatus('qc_completed');
      setMic((prev) => prev ? ({
        ...prev,
        ...payload,
        qcFiles: uploadedQcFiles,
        vehicleImageProofFiles: uploadedVehicleImageProofFiles,
        missingDamageFiles: uploadedMissingDamageFiles
      }) : prev);
      alert(emailQueued
        ? 'QC completed successfully and inward email queued.'
        : 'QC completed successfully, but the inward email could not be queued.'
      );
    } catch (error) {
      console.error("Error saving inbound QC:", error);
      alert("Failed to save QC: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const isCompleted = currentStatus === 'qc_completed';

  if (loading) return <Spinner />;

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button
            onClick={() => navigate('/ops/inbound-qc')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FileCheck2 size={26} color="#059669" /> Inbound QC Detail
            </h2>
            <p style={{ margin: '6px 0 0 0', color: '#64748b' }}>
              Review the completed MIC below and upload QC documents.
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          {isSaving && <Loader2 className="animate-spin" size={22} color="#2563eb" />}
          {!isCompleted && (
            <button onClick={handleConfirmQc} disabled={isSaving} style={confirmBtnStyle}>
              <CheckCircle size={18} /> Confirm QC
            </button>
          )}
          {isCompleted && (
            <div style={completedPillStyle}>QC COMPLETED</div>
          )}
        </div>
      </div>

      <MICPreviewContent
        micData={mic}
        customer={customer}
        warehouses={warehouses}
      />

      {!isCompleted ? (
        <div style={uploadCardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
            <Upload size={20} color="#2563eb" />
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#1e293b' }}>
              Inwards QC Details
            </h3>
          </div>
          <p style={{ margin: '0 0 16px 0', color: '#64748b', fontSize: '14px' }}>
            Attach the QC scan or supporting inward inspection files here.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div>
              <label style={labelStyle}>Upload QC</label>
              <MultiFileUpload
                value={qcFiles.length > 0 ? qcFiles : existingQcFiles}
                onFilesChange={(files) => {
                  setQcFiles(files);
                  clearValidationError('qcFiles');
                }}
                uploading={isSaving}
                externalError={validationErrors.qcFiles ? 'QC upload is required.' : ''}
              />
            </div>

            <div>
              <label style={labelStyle}>QC Remarks</label>
              <textarea
                value={qcRemarks}
                onChange={(e) => {
                  setQcRemarks(e.target.value);
                  clearValidationError('qcRemarks');
                }}
                disabled={isCompleted}
                placeholder="Enter QC observations, notes, or remarks..."
                style={getFieldStyle('qcRemarks', textareaStyle)}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginTop: '20px' }}>
            <div>
              <label style={labelStyle}>Vehicle Type</label>
              <select value={vehicleType} onChange={(e) => { setVehicleType(e.target.value); clearValidationError('vehicleType'); }} style={getFieldStyle('vehicleType', inputStyle)}>
                <option value="">Select vehicle type...</option>
                {vehicleTypeOptions.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Transport</label>
              <select value={transportName} onChange={(e) => { setTransportName(e.target.value); clearValidationError('transportName'); }} style={getFieldStyle('transportName', inputStyle)}>
                <option value="">Select transport...</option>
                {transportOptions.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Weight (in kgs)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={weightInKgs}
                onChange={(e) => {
                  setWeightInKgs(e.target.value);
                  clearValidationError('weightInKgs');
                }}
                style={getFieldStyle('weightInKgs', inputStyle)}
                placeholder="Enter weight"
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
            <div style={sectionBoxStyle}>
              <div style={sectionSubheadingStyle}>Vehicle Proof</div>
              <label style={labelStyle}>Vehicle Image Proof</label>
              <MultiFileUpload
                value={vehicleImageProofFiles.length > 0 ? vehicleImageProofFiles : existingVehicleImageProofFiles}
                onFilesChange={(files) => {
                  setVehicleImageProofFiles(files);
                  clearValidationError('vehicleImageProofFiles');
                }}
                multiple={false}
                maxFiles={1}
                uploading={isSaving}
                helperText="Single file only."
                externalError={validationErrors.vehicleImageProofFiles ? 'Vehicle image proof is required.' : ''}
              />
            </div>

            <div style={sectionBoxStyle}>
              <div style={sectionSubheadingStyle}>Missing / Damages</div>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Missing / Damages</label>
                <select value={missingDamages} onChange={(e) => { setMissingDamages(e.target.value); clearValidationError('missingDamages'); clearValidationError('missingDamageFiles'); }} style={getFieldStyle('missingDamages', inputStyle)}>
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>

              {missingDamages === 'yes' && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Missing / Damages Images</label>
                  <MultiFileUpload
                    value={missingDamageFiles.length > 0 ? missingDamageFiles : existingMissingDamageFiles}
                    onFilesChange={(files) => {
                      setMissingDamageFiles(files);
                      clearValidationError('missingDamageFiles');
                    }}
                    uploading={isSaving}
                    helperText="Upload multiple supporting images/files."
                    externalError={validationErrors.missingDamageFiles ? 'Missing / damages files are required when the answer is Yes.' : ''}
                  />
                </div>
              )}

              <label style={labelStyle}>Missing / Damages</label>
              <div style={checklistWrapStyle}>
                {checklistFields.map((field) => (
                  <label key={field.key} style={getFieldStyle(field.key, checklistItemStyle)}>
                    <input
                      type="checkbox"
                      checked={Boolean(inwardQcChecklist[field.key])}
                      onChange={() => handleChecklistChange(field.key)}
                      style={{ width: '16px', height: '16px', marginTop: '2px' }}
                    />
                    <span>{field.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: '20px', backgroundColor: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '800', color: '#1e293b' }}>
            QC Completion Details
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '16px', marginBottom: '20px' }}>
            <div><div style={metaLabelStyle}>Vehicle Type</div><div style={metaValueStyle}>{vehicleType || '---'}</div></div>
            <div><div style={metaLabelStyle}>Transport</div><div style={metaValueStyle}>{transportName || '---'}</div></div>
            <div><div style={metaLabelStyle}>Weight (in kgs)</div><div style={metaValueStyle}>{weightInKgs || '---'}</div></div>
            <div><div style={metaLabelStyle}>Missing / Damages</div><div style={metaValueStyle}>{missingDamages || '---'}</div></div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>QC Remarks</div>
              <div style={remarksDisplayStyle}>
                {qcRemarks || '---'}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>Uploaded QC Files</div>
              {existingQcFiles.length > 0 ? (
                <div style={{ display: 'grid', gap: '8px' }}>
                  {existingQcFiles.map((file, index) => (
                    <a
                      key={`${file.url || file.name || 'qc-file'}_${index}`}
                      href={file.url}
                      target="_blank"
                      rel="noreferrer"
                      style={savedFileStyle}
                    >
                      <FileText size={18} color="#64748b" />
                      <div style={{ overflow: 'hidden' }}>
                        <div style={{ fontSize: '13px', fontWeight: '700', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {file.name || 'Attachment'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{file.type || 'file'}</div>
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <div style={remarksDisplayStyle}>No files attached.</div>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>Vehicle Image Proof</div>
              {existingVehicleImageProofFiles.length > 0 ? (
                <div style={{ display: 'grid', gap: '8px' }}>
                  {existingVehicleImageProofFiles.map((file, index) => (
                    <a
                      key={`${file.url || file.name || 'vehicle-proof'}_${index}`}
                      href={file.url}
                      target="_blank"
                      rel="noreferrer"
                      style={savedFileStyle}
                    >
                      <FileText size={18} color="#64748b" />
                      <div style={{ overflow: 'hidden' }}>
                        <div style={{ fontSize: '13px', fontWeight: '700', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {file.name || 'Attachment'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{file.type || 'file'}</div>
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <div style={remarksDisplayStyle}>No file attached.</div>
              )}
            </div>

            <div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>Missing / Damages Files</div>
              {existingMissingDamageFiles.length > 0 ? (
                <div style={{ display: 'grid', gap: '8px' }}>
                  {existingMissingDamageFiles.map((file, index) => (
                    <a
                      key={`${file.url || file.name || 'missing-damage'}_${index}`}
                      href={file.url}
                      target="_blank"
                      rel="noreferrer"
                      style={savedFileStyle}
                    >
                      <FileText size={18} color="#64748b" />
                      <div style={{ overflow: 'hidden' }}>
                        <div style={{ fontSize: '13px', fontWeight: '700', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {file.name || 'Attachment'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{file.type || 'file'}</div>
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <div style={remarksDisplayStyle}>No files attached.</div>
              )}
            </div>
          </div>

          <div style={{ marginTop: '20px' }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>Checklist</div>
            <div style={checklistWrapStyle}>
              {checklistFields.map((field) => (
                <div key={field.key} style={completedChecklistItemStyle}>
                  <span style={{ fontWeight: '700', color: inwardQcChecklist[field.key] ? '#166534' : '#64748b' }}>
                    {inwardQcChecklist[field.key] ? 'Yes' : 'No'}
                  </span>
                  <span>{field.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '20px',
  marginBottom: '20px'
};

const uploadCardStyle = {
  marginTop: '24px',
  backgroundColor: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: '12px',
  padding: '24px',
  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.08)'
};

const confirmBtnStyle = {
  backgroundColor: '#059669',
  color: 'white',
  border: 'none',
  padding: '10px 18px',
  borderRadius: '8px',
  fontWeight: '700',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer'
};

const completedPillStyle = {
  backgroundColor: '#f0fdf4',
  color: '#166534',
  padding: '10px 16px',
  borderRadius: '999px',
  border: '1px solid #bbf7d0',
  fontSize: '12px',
  fontWeight: '800',
  letterSpacing: '0.04em'
};

const labelStyle = {
  display: 'block',
  marginBottom: '8px',
  fontSize: '13px',
  fontWeight: '700',
  color: '#475569'
};

const inputStyle = {
  width: '100%',
  minHeight: '44px',
  padding: '10px 12px',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
  backgroundColor: '#fff'
};
const errorInputStyle = {
  border: '1px solid #dc2626',
  boxShadow: '0 0 0 1px #dc2626'
};

const textareaStyle = {
  width: '100%',
  minHeight: '110px',
  padding: '12px',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
  resize: 'vertical',
  backgroundColor: '#fff'
};

const remarksDisplayStyle = {
  backgroundColor: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: '10px',
  padding: '12px',
  minHeight: '90px',
  whiteSpace: 'pre-wrap',
  color: '#0f172a',
  fontSize: '13px'
};

const savedFileStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '10px 12px',
  border: '1px solid #e2e8f0',
  borderRadius: '10px',
  textDecoration: 'none',
  color: '#0f172a',
  backgroundColor: '#ffffff'
};

const checklistWrapStyle = {
  display: 'grid',
  gap: '10px',
  backgroundColor: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: '10px',
  padding: '14px'
};

const checklistItemStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
  fontSize: '13px',
  color: '#1e293b',
  padding: '8px',
  borderRadius: '8px'
};

const completedChecklistItemStyle = {
  display: 'grid',
  gridTemplateColumns: '56px 1fr',
  gap: '10px',
  fontSize: '13px',
  color: '#1e293b'
};

const metaLabelStyle = {
  fontSize: '11px',
  fontWeight: '700',
  color: '#64748b',
  textTransform: 'uppercase',
  marginBottom: '6px'
};

const metaValueStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#0f172a'
};
const sectionBoxStyle = {
  backgroundColor: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: '12px',
  padding: '16px'
};
const sectionSubheadingStyle = {
  fontSize: '15px',
  fontWeight: '800',
  color: '#1e293b',
  marginBottom: '14px'
};

export default InboundQCDetail;
